/**
 * Smart RTL/LTR Input Direction Helper
 *
 * Global, event-based system that automatically detects Arabic text in
 * INPUT and TEXTAREA elements and switches their typing direction accordingly.
 *
 * - Arabic characters → direction: rtl, text-align: right
 * - Latin / numeric → direction: ltr, text-align: left
 * - Empty → removes inline direction overrides and keeps default behavior
 * - Skips password, checkbox, radio, hidden, range, color, file inputs
 * - Supports paste events
 * - Works with dynamically rendered elements (event delegation)
 * - Memory-leak safe with proper cleanup
 *
 * Usage:
 *   import { initSmartDirection, destroySmartDirection } from './helper/smartDirectionHelper';
 *   // on mount
 *   const cleanup = initSmartDirection();
 *   // on unmount
 *   cleanup();
 */

const ARABIC_REGEX = /[\u0600-\u06FF]/;

/**
 * Input types that should NOT be affected by direction changes.
 */
const EXCLUDED_INPUT_TYPES = new Set([
  'password',
  'checkbox',
  'radio',
  'hidden',
  'range',
  'color',
  'file',
  'submit',
  'reset',
  'button',
  'image',
]);

/**
 * Check whether an element is a text-entry input or textarea that we should handle.
 */
function isTextEntryElement(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el.type || 'text').toLowerCase();
    return !EXCLUDED_INPUT_TYPES.has(type);
  }
  return false;
}

/**
 * Apply the correct direction and text-align to the given element based on its value.
 */
function applyDirection(el) {
  const value = el.value || '';
  if (value.length === 0) {
    // When the field is empty, remove our inline overrides so the default behaviour is preserved.
    el.style.removeProperty('direction');
    el.style.removeProperty('text-align');
    return;
  }

  if (ARABIC_REGEX.test(value)) {
    el.style.direction = 'rtl';
    el.style.textAlign = 'right';
  } else {
    el.style.direction = 'ltr';
    el.style.textAlign = 'left';
  }
}

function isArabicLanguageActive() {
  const lang = document.documentElement.lang || '';
  return lang.toLowerCase().startsWith('ar');
}

/**
 * Shared handler used for input, paste, and change events.
 * Uses event delegation on document so it automatically works with
 * dynamically created elements.
 */
function handleInputEvent(e) {
  // Only activate for Arabic language — other languages keep default behavior
  if (!isArabicLanguageActive()) return;

  const el = e.target;
  if (!isTextEntryElement(el)) return;

  // For paste events the value isn't updated yet at the time the event fires,
  // so we defer to the next macrotask to ensure the browser has processed the paste.
  if (e.type === 'paste') {
    setTimeout(() => applyDirection(el), 0);
  } else {
    applyDirection(el);
  }
}

let _initialized = false;

/**
 * Initialize the smart direction system.
 * Should be called once at application root level.
 *
 * @returns {Function} cleanup – call this to tear everything down (e.g. on unmount).
 */
export function initSmartDirection() {
  if (_initialized) {
    // Already running – return a no-op cleanup to stay idempotent.
    return () => { };
  }

  document.addEventListener('input', handleInputEvent, true);
  document.addEventListener('paste', handleInputEvent, true);
  document.addEventListener('change', handleInputEvent, true);
  document.addEventListener('focusin', handleInputEvent, true);

  _initialized = true;

  return destroySmartDirection;
}

/**
 * Tear down all listeners that were set up by `initSmartDirection`.
 */
export function destroySmartDirection() {
  if (!_initialized) return;

  document.removeEventListener('input', handleInputEvent, true);
  document.removeEventListener('paste', handleInputEvent, true);
  document.removeEventListener('change', handleInputEvent, true);
  document.removeEventListener('focusin', handleInputEvent, true);

  _initialized = false;
}

export function refreshSmartDirection() {
  applyDirectionToExistingInputs();
}

function applyDirectionToExistingInputs() {
  // Only activate for Arabic language — other languages keep default behavior
  if (!isArabicLanguageActive()) return;

  document.querySelectorAll('input, textarea').forEach((el) => {
    if (isTextEntryElement(el)) {
      applyDirection(el);
    }
  });
}
